import { MovieGenre } from './movie-genre.enum'

import * as moment from 'moment'

export enum ShowtimeType {
  None           = 0,
  CC             = 1 << 0,
  DVS            = 1 << 1,
  FrenchVersion  = 1 << 2,
  UltraAVX       = 1 << 3,
  DBOX           = 1 << 4,
  ThreeDimension = 1 << 5,
  IMAX           = 1 << 6,
}

export namespace ShowtimeType {
  export function getType(stringToParse: string): ShowtimeType {
    let result: ShowtimeType = ShowtimeType.None

    if (!stringToParse) {
      return result
    }

    const types: string[] = stringToParse.split('|')

    for (const typeString of types) {
      switch (typeString) {
        case 'Version française':
        case 'French Version':
          result |= ShowtimeType.FrenchVersion
          break
        case 'Closed Captioned':
          result |= ShowtimeType.CC
          break
        case '3D':
          result |= ShowtimeType.ThreeDimension
          break
        case 'DVS':
        case 'DVS®':
        case 'Descriptive Video Services':
          result |= ShowtimeType.DVS
          break
        case 'Closed Caption & Descriptive Video':
        case 'CC/DVS':
          result |= ShowtimeType.CC | ShowtimeType.DVS
          break
        case 'UltraAVX':
          result |= ShowtimeType.UltraAVX
          break
        case 'D-BOX':
          result |= ShowtimeType.DBOX
          break
        default:
          break
      }
    }

    return result
  }

  export function toString(type: ShowtimeType) {
    let result = ''

    if (type & ShowtimeType.FrenchVersion) {
      result += 'Version française '
    } else {
      result += 'Version originale '
    }

    if (type & ShowtimeType.UltraAVX) {
      result += 'UltraAVX '
    } else if (type & ShowtimeType.ThreeDimension) {
      result += '3D '
    }

    if (type & ShowtimeType.IMAX) {
      result += 'IMAX'
    }
    if (type & ShowtimeType.DBOX) {
      result += 'D-BOX '
    }

    if (type & ShowtimeType.CC & ShowtimeType.DVS) {
      result += '(CC/DVS) '
    } else if (type & ShowtimeType.CC) {
      result += '(CC) '
    } else if (type & ShowtimeType.DVS) {
      result += '(DVS) '
    }

    return result
  }

}

export interface LangToDataMap {
    [lang: string]: any
}

export class Theatre {
  id: number
  name?: string
  distance?: number
}

export class ShowtimesByTheatre {
  theatre: Theatre
  showtimes: Showtime[] = []

  constructor(theatre?: Theatre) {
    this.theatre = theatre
    this.showtimes = []
  }
}

export class Showtime {
  datetime: moment.Moment
  type: ShowtimeType
}

export class Movie {
    rootId: string
    titles: LangToDataMap = {}
    shortDescriptions: LangToDataMap = {}
    longDescriptions: LangToDataMap = {}
    bannerUrls: LangToDataMap = {}
    preferredImage = ''
    sceneImages: string[] = []
    duration: number = -1
    rating: number = -1
    genres: string[] = []
    directors: string[] = []
    topCast: string[] = []
    showtimesByTheatre?: ShowtimesByTheatre[] = []

    constructor (private defaultLang: string = 'en') { }

    public deserialize(input: any): Movie {
      let titleLang = input.titleLang.toLowerCase()
      titleLang = titleLang.split('-')[0]

      this.rootId = input.rootId
      if (!this.titles[titleLang] ||
          input.title.length < this.titles[titleLang].length) {
        this.titles[titleLang] = input.title

        if (titleLang === this.defaultLang || !this.titles['default']) {
          this.titles['default'] = input.title
        }
      }


      if (!this.shortDescriptions[titleLang]) {
        this.shortDescriptions[titleLang] = input.shortDescription
      }

      if (!this.longDescriptions[titleLang]) {
        this.longDescriptions[titleLang] = input.longDescription
      }

      if (this.rating === -1 && input.qualityRating && input.qualityRating.value) {
        this.rating = input.qualityRating.value
      }

      if (this.duration === -1 && input.runTime) {
        this.duration = this.computeDuration(input.runTime)
      }

      if (this.genres.length === 0 && input.genres) {
        this.genres = input.genres
      }

      if (this.directors.length === 0 && input.directors) {
        this.directors = input.directors
      }

      if (this.topCast.length === 0 && input.topCast) {
        this.topCast = input.topCast
      }

      if (this.preferredImage.length === 0 && input.preferredImage) {
        this.preferredImage = input.preferredImage.uri
      }

      if (this.sceneImages.length === 0 && input.sceneImages) {
        this.sceneImages = input.sceneImages
      }

      if (!this.bannerUrls[titleLang] && input.preferredImage) {
        this.bannerUrls[titleLang] = input.preferredImage.uri

        if (titleLang === this.defaultLang || !this.bannerUrls['default']) {
          this.bannerUrls['default'] = input.preferredImage.uri
        }
      }

      if (input.showtimes) {
        for (const showtimeByTheatre of input.showtimes) {
          const theatre: Theatre = {
            id: showtimeByTheatre.theater.theatreId,
            name: showtimeByTheatre.theater.name,
          }

          if (showtimeByTheatre.theater.location) {
            theatre.distance = showtimeByTheatre.theater.location.distance
          }

          // Check if theatre already exists
          let index: number = this.showtimesByTheatre.findIndex((showtime: ShowtimesByTheatre) => showtime.theatre.id === theatre.id)

          // If not, add it to showtimes
          if (index === -1) {
            const theatreShowtime = {
              theatre: theatre,
              showtimes: []
            }

            this.showtimesByTheatre.push(theatreShowtime)
            index = this.showtimesByTheatre.length - 1
          }

          // Add showtime to the corresponding theatre
          const showtime: Showtime = {
            datetime: moment(showtimeByTheatre.dateTime),
            // datetime: new Date(showtimeByTheatre.dateTime),
            type: ShowtimeType.getType(showtimeByTheatre.quals)
          }

          // add 3D if the title ended with the word 3D
          if (input.title.includes('3D')) {
            showtime.type |= ShowtimeType.ThreeDimension
          }

          if (input.title.includes('IMAX')) {
            showtime.type |= ShowtimeType.IMAX
          }

          if (titleLang === 'fr') {
            showtime.type |= ShowtimeType.FrenchVersion
          }

          this.showtimesByTheatre[index].showtimes.push(showtime)
          // console.log(showtime);

          // this.showtimesByTheatre[index].showtimes.sort()
        }
      }
      return this
    }

    public getTitle(lang: string): string {
      return this.getTranslateAttribute('titles', lang)
    }

    public getShortDescription(lang: string): string {
      return this.getTranslateAttribute('shortDescriptions', lang)
    }

    public getLongDescription(lang: string): string {
      return this.getTranslateAttribute('longDescriptions', lang)
    }

    public getBannerUrl(lang: string): string {
      return this.getTranslateAttribute('bannerUrls', lang)

      // if (!url.startsWith('http://')) {
      //   url = 'http://developer.tmsimg.com/' + url + '?api_key=9h6jvc6de4ez8xwy53khs5bw'
      // }

      // return url
    }

    public getDaysWithShowtimes(theatreId: number): number[] {
      const daysWithShowtimes: number[] = []

      const theatreShowtimes = this.showtimesByTheatre.find((showtime: ShowtimesByTheatre) => {
        return showtime.theatre.id === theatreId
      })

      for (const showtime of theatreShowtimes.showtimes) {
        const day = showtime.datetime.day()
        if (daysWithShowtimes.indexOf(day) === -1) {
          daysWithShowtimes.push(day)
        }
      }

      return daysWithShowtimes
    }

    private getTranslateAttribute(attribute: string, lang: string) {
      if (this[attribute][lang]) {
        return this[attribute][lang]
      } else if (this[attribute][this.defaultLang]) {
        return this[attribute][this.defaultLang]
      }

      const keys = Object.keys(this[attribute])
      return this[attribute][keys[0]]
    }

    private computeDuration(runTime: string): number {
      // tslint:disable-next-line:max-line-length
      const regex = /P((([0-9]*\.?[0-9]*)Y)?(([0-9]*\.?[0-9]*)M)?(([0-9]*\.?[0-9]*)W)?(([0-9]*\.?[0-9]*)D)?)?(T(([0-9]*\.?[0-9]*)H)?(([0-9]*\.?[0-9]*)M)?(([0-9]*\.?[0-9]*)S)?)?/

      const matches = runTime.match(regex)

      const hours = matches[12]
      const minutes = matches[14]
      const totalMinutes = parseInt(hours, 10) * 60 + parseInt(minutes, 10)

      return totalMinutes
    }
}



// WEBPACK FOOTER //
// C:/Users/florian.gadal/Workspace/cinenow-web/src/app/movies/shared/movie.model.ts